<template>
  <layout-basic>
    <section class="faq-page">
      <div class="faq-hero">
        <h1 class="font-sora">Everything You Need to Know About IMPROVE</h1>
        <p>{{ faq.intro }}</p>
      </div>

      <!-- The document's six sections, in its order. Answers are the document's
           own paragraphs and lists (src/data/faq.js is generated from it). -->
      <div class="faq-list-wrap">
        <section v-for="(section, si) in faq.sections" :key="section.title" class="faq-section">
          <h2 class="font-sora faq-section__title">{{ section.title }}</h2>
          <div class="faq-list">
            <div
              v-for="(item, index) in section.items"
              :key="item.question"
              class="faq-item"
              :class="{ open: isOpen(si, index) }"
            >
              <button
                type="button"
                class="faq-question"
                :aria-expanded="isOpen(si, index) ? 'true' : 'false'"
                @click="toggle(si, index)"
              >
                <span>{{ item.question }}</span>
                <i :class="isOpen(si, index) ? 'fas fa-minus' : 'fas fa-plus'" aria-hidden="true"></i>
              </button>
              <div v-show="isOpen(si, index)" class="faq-answer" v-html="item.answer"></div>
            </div>
          </div>
        </section>

        <section class="faq-section faq-steps">
          <h2 class="font-sora faq-section__title">What Happens After I Buy?</h2>
          <p class="faq-steps__lead">Getting started with IMPROVE is simple.</p>
          <ol class="faq-steps__list">
            <li v-for="(step, n) in faq.steps" :key="step.title" class="faq-step">
              <span class="faq-step__num">{{ n + 1 }}</span>
              <div class="faq-step__body">
                <h3 class="font-sora">{{ step.title }}</h3>
                <div v-html="step.body"></div>
              </div>
            </li>
          </ol>
        </section>

        <section class="faq-section faq-ready">
          <h2 class="font-sora faq-section__title">Ready to Get Started?</h2>
          <p class="faq-steps__lead">Choose the option that's right for you.</p>
          <div class="faq-ready__grid">
            <component
              :is="cta.to === 'experience' ? 'a' : 'router-link'"
              v-for="cta in faq.ctas"
              :key="cta.title"
              :to="cta.to === 'experience' ? undefined : cta.to"
              :href="cta.to === 'experience' ? experienceHref : undefined"
              :rel="cta.to === 'experience' ? 'noopener' : undefined"
              class="faq-ready__card"
            >
              <strong>{{ cta.title }}</strong>
              <span>{{ cta.body }}</span>
            </component>
          </div>
        </section>
      </div>

      <standard-cta
        title="Still have a question?"
        :lead="faq.still + ' Email info@improve.online.'"
      />
    </section>
  </layout-basic>
</template>

<script>
import LayoutBasic from '@/layouts/Dashboard';
import StandardCta from '@/components/marketing/StandardCta.vue';
import { screensize } from '../../utils/screensize';
import { getExperienceHref } from '@/config/ctas';
import faq from '@/data/faq';

const getCurrentScreenSize = screensize();

export default {
  name: 'FAQ',
  components: {
    LayoutBasic,
    StandardCta,
  },
  data() {
    return {
      screenSize: getCurrentScreenSize(),
      faq,
      // "section-index:item-index" of the one open answer; the first question opens by default.
      open: '0:0',
    };
  },
  metaInfo() {
    return {
      title: 'FAQs | Everything You Need to Know About IMPROVE',
      meta: [
        {
          name: 'description',
          content:
            'What IMPROVE is, who it is for, what it costs, how to get started and what happens after you buy. The questions growing businesses ask us most.',
        },
      ],
      link: [{ rel: 'canonical', href: 'https://www.improve.online/faq' }],
    };
  },
  computed: {
    experienceHref() {
      return getExperienceHref();
    },
  },
  mounted() {
    window.addEventListener('resize', this.handleResize);
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.handleResize);
  },
  methods: {
    isOpen(si, index) {
      return this.open === si + ':' + index;
    },
    toggle(si, index) {
      const key = si + ':' + index;
      this.open = this.open === key ? null : key;
    },
    handleResize() {
      this.screenSize = getCurrentScreenSize();
    },
  },
};
</script>

<style scoped>
.faq-page {
  background: #ffffff;
}

.faq-hero {
  background-image: url(/static/single-page/banner.png);
  background-position: center;
  background-repeat: no-repeat;
  background-size: cover;
  text-align: center;
  padding: 72px 20px 48px;
}

.faq-hero h1 {
  margin: 0 0 12px;
  color: #002458;
  font-size: 48px;
  font-weight: 700;
  line-height: 1.15;
}

.faq-hero p {
  margin: 0 auto;
  max-width: 560px;
  color: #6b7280;
  font-size: 17px;
  line-height: 1.5;
}

.faq-list-wrap {
  width: 100%;
  background: #ffffff;
  padding: 24px 16px 64px;
}

.faq-list {
  width: 100%;
  max-width: 820px;
  margin: 0 auto;
}

.faq-item {
  border-bottom: 1px solid #d9d9d9;
}

.faq-question {
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  padding: 18px 4px;
  border: 0;
  background: transparent;
  cursor: pointer;
  text-align: left;
  font-family: inherit;
  font-size: 18px;
  font-weight: 600;
  color: #08a0a8;
  line-height: 1.35;
}

.faq-question span {
  flex: 1 1 auto;
  min-width: 0;
}

.faq-question i {
  flex: 0 0 auto;
  color: #002458;
  font-size: 14px;
}

.faq-question:hover {
  color: #078f96;
}

.faq-answer {
  padding: 0 4px 18px;
}

.faq-answer >>> p,
.faq-answer /deep/ p {
  margin: 0 0 10px;
  color: #334155;
  font-size: 16px;
  line-height: 1.55;
}

.faq-answer >>> ul,
.faq-answer /deep/ ul {
  margin: 0 0 10px;
  padding-left: 22px;
  color: #334155;
  font-size: 16px;
  line-height: 1.55;
}

.faq-section + .faq-section {
  margin-top: 36px;
}

.faq-section__title {
  color: var(--brand-navy, #002458);
  font-size: 24px;
  font-weight: 700;
  margin: 0 0 14px;
}

.faq-steps__lead {
  color: #334155;
  margin: 0 0 16px;
}

.faq-steps__list {
  list-style: none;
  padding: 0;
  margin: 0;
  display: grid;
  gap: 14px;
}

.faq-step {
  display: flex;
  gap: 14px;
  background: #fff;
  border: 1px solid #e6ebf1;
  border-radius: 12px;
  padding: 18px;
}

.faq-step__num {
  flex: 0 0 34px;
  height: 34px;
  border-radius: 50%;
  background: var(--brand-teal, #08a0a8);
  color: #fff;
  font-weight: 800;
  display: flex;
  align-items: center;
  justify-content: center;
}

.faq-step__body h3 {
  color: var(--brand-navy, #002458);
  font-size: 17px;
  margin: 4px 0 8px;
}

.faq-step__body >>> p,
.faq-step__body /deep/ p,
.faq-step__body >>> ul,
.faq-step__body /deep/ ul {
  color: #334155;
  font-size: 15.5px;
  line-height: 1.5;
  margin: 0 0 8px;
}

.faq-ready__grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 14px;
}

.faq-ready__card {
  display: flex;
  flex-direction: column;
  gap: 6px;
  background: #fff;
  border: 2px solid var(--brand-teal, #08a0a8);
  border-radius: 12px;
  padding: 20px 18px;
  text-decoration: none;
  color: var(--brand-navy, #002458);
}

.faq-ready__card:hover {
  background: #f3fbfb;
  color: var(--brand-navy, #002458);
}

.faq-ready__card strong {
  font-size: 17px;
}

.faq-ready__card span {
  color: var(--brand-grey, #6b7280);
  font-size: 14px;
  line-height: 1.45;
}

@media screen and (max-width: 800px) {
  .faq-ready__grid {
    grid-template-columns: 1fr;
  }
}

.faq-answer >>> a,
.faq-answer /deep/ a {
  color: #08a0a8;
  font-weight: 600;
}

@media screen and (max-width: 600px) {
  .faq-hero {
    padding: 48px 16px 32px;
  }

  .faq-hero h1 {
    font-size: 36px;
  }

  .faq-question {
    font-size: 16px;
  }
}
</style>
